import { ShieldCheck, Mail, Smartphone } from 'lucide-react';
import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/ui/elements/button/Button';

type PasswordOtp = {
  setStep: (step: string) => void;
};

function PasswordOtp({ setStep }: PasswordOtp) {
  const [otp, setOtp] = useState('');
  const { t } = useTranslation('login');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow only numbers, max 6 digits
    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(val);
  };

  const isOtpComplete = otp.length === 6;

  return (
    <div className="">
      <div
        data-slot="dialog-header"
        className="flex flex-col gap-2 text-center sm:text-left"
      >
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-slate-900 rounded-full flex items-center justify-center">
            <ShieldCheck className="w-8 h-8 text-white" aria-hidden="true" />
          </div>
        </div>

        {/* Title */}
        <h2
          id="radix-:r1f:"
          data-slot="dialog-title"
          className="font-semibold text-center text-2xl text-slate-900"
        >
          {t('verifyYourIdentity')}
        </h2>

        {/* Description */}
        <p
          id="radix-:r1g:"
          data-slot="dialog-description"
          className="text-sm text-center text-slate-500"
        >
          {t('enterVerificationCode')}
        </p>

        {/* Badges */}
        <div className="flex items-center justify-center gap-2 pt-3">
          <span
            data-slot="badge"
            className="inline-flex items-center justify-center border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden gap-1 bg-blue-50 text-blue-700 border-blue-200 rounded-full"
          >
            <Mail className="w-3 h-3" aria-hidden="true" />
            {t('emailOtp')}
          </span>

          <span
            data-slot="badge"
            className="inline-flex items-center justify-center border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden gap-1 bg-green-50 text-green-700 border-green-200 rounded-full"
          >
            <Smartphone className="w-3 h-3" aria-hidden="true" />
            {t('smsBackup')}
          </span>
        </div>
      </div>
      {/*  */}
      <div className="space-y-5 py-4">
        {/* Code sent info */}
        <div className="text-sm text-center text-slate-600 bg-slate-50 p-3 rounded-lg border border-slate-200">
          {t('codeSentTo')}{' '}
          <strong className="text-slate-900">tgds@hbnsc.com</strong>
        </div>

        {/* OTP Input container */}
        <div className="flex justify-center">
          <div
            data-input-otp-container="true"
            onClick={() => inputRef.current?.focus()}
            className="flex items-center gap-2 has-disabled:opacity-50 relative cursor-text select-none"
            style={{ '--root-height': '51px' } as React.CSSProperties}
          >
            {/* OTP slots */}
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                data-slot="input-otp-slot"
                data-active={Math.min(otp.length, 5) === i && isFocused}
                className={`relative flex items-center justify-center border-y border-r text-sm bg-input-background transition-all outline-none first:rounded-l-md first:border-l last:rounded-r-md data-[active=true]:z-10 data-[active=true]:ring-[3px] 
                    data-[active=true]:border-ring data-[active=true]:ring-ring/50 
                    data-[active=true]:aria-invalid:ring-destructive/20 dark:data-[active=true]:aria-invalid:ring-destructive/40 
                    aria-invalid:border-destructive dark:bg-input/30 h-12 w-12 border-lt-slate-300`}
              >
                {otp[i] || ''}
              </div>
            ))}

            {/* Hidden input overlay */}
            <input
              type="text"
              autoComplete="one-time-code"
              inputMode="numeric"
              pattern="\d*"
              maxLength={6}
              value={otp}
              ref={inputRef}
              onChange={handleOtpChange}
              autoFocus
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              className="absolute inset-0 w-full h-full opacity-0 cursor-text"
            />
          </div>
        </div>

        {/* Resend */}
        <div className="text-center text-sm text-slate-600">
          {t('noCodeReceived')}{' '}
          <button className="text-blue-600 hover:underline">
            {t('resendCode')}
          </button>
        </div>

        {/* Verify & Continue button */}

        <Button
          variant="verify" // or "primaryBlue" if you prefer that name
          size="full"
          disabled={!isOtpComplete}
          onClick={() => setStep('resetPwd')}
        >
          {t('verifyAndContinue')}
        </Button>

        {/* Cancel button */}
        <Button variant="outlineGray" size="fullSm" type="button">
          {t('cancel')}
        </Button>

        {/* Expiry info */}
        <p className="text-xs text-center text-slate-500 pt-2">
          {t('codeWillExpire')}
        </p>
      </div>
      {/*  */}
    </div>
  );
}

export default PasswordOtp;
